import React, { useEffect, useMemo, useRef, useState } from "react";
import usmApi, { USM_BASE, USM_TOKEN_KEY } from "@/lib/usmApi";
import { toast } from "sonner";
import { Printer, Download, X, Loader2 } from "lucide-react";

/**
 * Toplu (Batch) Etiket Yazdırma Diyaloğu.
 *
 * Workflow:
 *   1. Kullanıcı InventoryList'te ürünleri çoklu seçer.
 *   2. Bir etiket profili seçer ve her ürün için kopya adedini belirler.
 *   3. Her ürün için PNG label.png endpoint'inden çekilir (paralel batch).
 *   4. Tek bir yazdırma penceresinde tüm etiketler tek tek stacklenip yazıcıya gönderilir.
 *
 * Notlar:
 *   - Backend'de yeni endpoint açmıyoruz — varolan `/inventory/{id}/label.png`
 *     endpoint'ini paralel olarak çağırıyoruz (8'li gruplar halinde, ağı yormamak için).
 *   - Etiket boyutu seçili profilden gelir; tüm etiketler aynı boyutta basılır.
 */
export default function BatchLabelPrintDialog({ items, onClose }) {
  const [profiles, setProfiles] = useState([]);
  const [profileId, setProfileId] = useState("");
  const [qtyPerItem, setQtyPerItem] = useState(1);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const blobUrlsRef = useRef([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await usmApi.get("/labels/profiles");
        if (!mounted) return;
        const list = r.data || [];
        setProfiles(list);
        const def = list.find((p) => p.is_default) || list[0];
        setProfileId(def?.id || "");
      } catch (e) {
        toast.error("Etiket profilleri yüklenemedi");
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Revoke any blob URLs we created on unmount.
  useEffect(() => () => {
    blobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    blobUrlsRef.current = [];
  }, []);

  const profile = useMemo(() => profiles.find((p) => p.id === profileId), [profiles, profileId]);
  const totalLabels = items.length * Math.max(1, qtyPerItem);

  async function fetchLabel(itemId) {
    const token = localStorage.getItem(USM_TOKEN_KEY);
    const r = await fetch(`${USM_BASE}/inventory/${itemId}/label.png?profile=${encodeURIComponent(profileId)}&t=${Date.now()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) throw new Error(`Etiket alınamadı (${itemId})`);
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    blobUrlsRef.current.push(url);
    return url;
  }

  // Run promises in chunks of N to avoid hammering the server.
  async function runInChunks(arr, n, runner) {
    const out = new Array(arr.length);
    for (let i = 0; i < arr.length; i += n) {
      const slice = arr.slice(i, i + n);
      const sliceResults = await Promise.all(slice.map(runner));
      for (let j = 0; j < slice.length; j++) out[i + j] = sliceResults[j];
      setProgress({ done: Math.min(arr.length, i + n), total: arr.length });
    }
    return out;
  }

  const handlePrint = async () => {
    if (!profileId || items.length === 0) return;
    setBusy(true);
    setProgress({ done: 0, total: items.length });
    try {
      const urls = await runInChunks(items, 6, async (it) => ({ item: it, url: await fetchLabel(it.id) }));
      const copies = urls.flatMap(({ item, url }) =>
        Array.from({ length: Math.max(1, qtyPerItem) }).map(() =>
          `<div class="lbl" data-stock="${item.stock_code || item.barcode || ""}"><img src="${url}" /></div>`
        )
      ).join("");

      const win = window.open("", "_blank", "width=600,height=500");
      if (!win) { toast.error("Yazdırma penceresi açılamadı. Pop-up'a izin verin."); return; }
      win.document.write(`<!doctype html>
<html><head><title>Toplu Etiket (${urls.length} ürün · ${urls.length * qtyPerItem} etiket)</title>
<style>
  @page { size: ${profile?.width_mm || 50}mm ${profile?.height_mm || 25}mm; margin: 0; }
  html, body { margin: 0; padding: 0; background: #fff; }
  .lbl { width: ${profile?.width_mm || 50}mm; height: ${profile?.height_mm || 25}mm; page-break-after: always; display: flex; align-items: center; justify-content: center; overflow: hidden; }
  .lbl:last-child { page-break-after: auto; }
  /* CRITICAL FOR BARCODE SCAN RELIABILITY — see LabelPrintDialog for the
   * detailed rationale. TL;DR: pixelated rendering preserves crisp 0/1
   * bar edges through the browser-to-thermal-printer pipeline. */
  img {
    width: ${profile?.width_mm || 50}mm;
    height: ${profile?.height_mm || 25}mm;
    object-fit: fill;
    display: block;
    image-rendering: pixelated;
    image-rendering: -webkit-optimize-contrast;
    image-rendering: crisp-edges;
  }
</style></head>
<body>${copies}
<script>
  window.addEventListener('load', function(){ setTimeout(function(){ window.print(); }, 200); });
  window.addEventListener('afterprint', function(){ window.close(); });
</script>
</body></html>`);
      win.document.close();
      toast.success(`${urls.length} ürün · ${urls.length * qtyPerItem} etiket yazıcıya gönderildi`);
    } catch (e) {
      toast.error(e.message || "Toplu yazdırma başarısız");
    } finally {
      setBusy(false);
    }
  };

  const handlePrintPdf = async () => {
    if (!profileId || items.length === 0) return;
    setBusy(true);
    setProgress({ done: 0, total: items.length });
    try {
      const token = localStorage.getItem(USM_TOKEN_KEY);
      // qtyPerItem copies → expand the ids list by N
      const expanded = items.flatMap((it) => Array(Math.max(1, qtyPerItem)).fill(it.id)).join(",");
      const r = await fetch(
        `${USM_BASE}/inventory/labels.pdf?ids=${encodeURIComponent(expanded)}&profile=${encodeURIComponent(profileId)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!r.ok) throw new Error("PDF oluşturulamadı");
      const blob = await r.blob();
      const pdfUrl = URL.createObjectURL(blob);
      blobUrlsRef.current.push(pdfUrl);
      const win = window.open(pdfUrl, "_blank");
      if (!win) {
        const a = document.createElement("a");
        a.href = pdfUrl;
        a.download = `etiketler_${totalLabels}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.info("Pop-up engellendi — PDF indirildi");
      } else {
        toast.success(`${totalLabels} etiket PDF olarak açıldı — Argox'a gönderin`);
      }
    } catch (e) {
      toast.error(e.message || "Toplu PDF başarısız");
    } finally {
      setBusy(false);
    }
  };

  const handleDownloadZip = async () => {
    // Pragmatic alternative without a zip lib: download each PNG individually.
    // (Acceptable for ≤50 items; larger batches should use a server-side zip.)
    if (!profileId || items.length === 0) return;
    setBusy(true);
    setProgress({ done: 0, total: items.length });
    try {
      let done = 0;
      for (const it of items) {
        const token = localStorage.getItem(USM_TOKEN_KEY);
        const r = await fetch(`${USM_BASE}/inventory/${it.id}/label.png?profile=${encodeURIComponent(profileId)}&download=true`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error("İndirme başarısız");
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${it.stock_code || it.barcode || it.id}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        done++;
        setProgress({ done, total: items.length });
      }
      toast.success(`${done} etiket indirildi`);
    } catch (e) {
      toast.error(e.message || "İndirme hatası");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={busy ? undefined : onClose}>
      <div
        data-testid="batch-label-dialog"
        className="bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-[#E7E5E4] flex items-center justify-between">
          <div>
            <h2 className="font-heading text-xl">Toplu Etiket Yazdır</h2>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#71717A] mt-0.5 font-mono">
              {items.length} ürün seçili · {totalLabels} etiket
            </p>
          </div>
          <button onClick={onClose} disabled={busy} className="text-[#71717A] hover:text-[#0A0A0A] disabled:opacity-40">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <Field label="Etiket Profili">
            <select
              data-testid="batch-label-profile-select"
              value={profileId}
              onChange={(e) => setProfileId(e.target.value)}
              className={inputCls}
              disabled={busy}
            >
              {profiles.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.width_mm}×{p.height_mm} mm · {p.barcode_type})
                </option>
              ))}
            </select>
          </Field>

          <Field label="Her Üründen Kopya Adedi">
            <div className="flex items-center gap-2">
              <button type="button" disabled={busy} onClick={() => setQtyPerItem((q) => Math.max(1, q - 1))} className="px-3 py-2 border border-[#E7E5E4] disabled:opacity-40">−</button>
              <input
                data-testid="batch-label-qty-input"
                type="number"
                min="1"
                max="20"
                value={qtyPerItem}
                disabled={busy}
                onChange={(e) => setQtyPerItem(Math.max(1, Math.min(20, parseInt(e.target.value || "1", 10))))}
                className={inputCls + " text-center max-w-[120px]"}
              />
              <button type="button" disabled={busy} onClick={() => setQtyPerItem((q) => Math.min(20, q + 1))} className="px-3 py-2 border border-[#E7E5E4] disabled:opacity-40">+</button>
            </div>
          </Field>

          {/* Selected items quick list */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-[#71717A] mb-1.5">Seçili Ürünler</div>
            <div className="border border-[#E7E5E4] max-h-44 overflow-y-auto divide-y divide-[#F5F5F4]">
              {items.slice(0, 40).map((it) => (
                <div key={it.id} className="px-3 py-2 text-[12.5px] flex items-baseline gap-3">
                  <span className="font-mono text-[#A1A1AA] w-28 shrink-0 truncate">{it.stock_code || it.barcode}</span>
                  <span className="flex-1 truncate">{it.name}</span>
                </div>
              ))}
              {items.length > 40 && (
                <div className="px-3 py-2 text-[11px] text-[#71717A]">
                  + {items.length - 40} ürün daha...
                </div>
              )}
            </div>
          </div>

          {busy && (
            <div className="bg-[#FAFAF9] border border-[#E7E5E4] px-4 py-3 text-[12px] text-[#27272A] flex items-center gap-2">
              <Loader2 size={14} className="animate-spin text-[#C8A96A]" />
              Etiketler hazırlanıyor… {progress.done} / {progress.total}
            </div>
          )}

          <div className="bg-[#FAFAF9] border border-[#F5F5F4] p-3 text-[11px] text-[#71717A] leading-relaxed">
            <p className="font-semibold text-[#0A0A0A] mb-1">Argox OS-216 için PDF Yazdır önerilir</p>
            <p>PDF, tüm etiketleri tek dosyada kayıpsız (CCITT G4) olarak Argox sürücüsüne gönderir. Tarayıcı yazdırma yedek seçenektir.</p>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[#E7E5E4] flex flex-wrap justify-end gap-3">
          <button
            onClick={handleDownloadZip}
            data-testid="batch-label-download-btn"
            disabled={busy || items.length === 0}
            className="text-xs uppercase tracking-[0.2em] px-5 py-3 border border-[#E7E5E4] hover:bg-[#FAFAF9] inline-flex items-center gap-2 disabled:opacity-40"
          >
            <Download size={14} /> PNG İndir
          </button>
          <button
            onClick={handlePrint}
            data-testid="batch-label-print-btn"
            disabled={busy || !profileId || items.length === 0}
            className="text-xs uppercase tracking-[0.2em] px-5 py-3 border border-[#E7E5E4] hover:bg-[#FAFAF9] disabled:opacity-40 inline-flex items-center gap-2"
          >
            <Printer size={14} /> Tarayıcı ile Yazdır
          </button>
          <button
            onClick={handlePrintPdf}
            data-testid="batch-label-print-pdf-btn"
            disabled={busy || !profileId || items.length === 0}
            className="bg-[#0A0A0A] disabled:opacity-40 text-white text-xs uppercase tracking-[0.2em] px-6 py-3 inline-flex items-center gap-2"
            title="Argox için önerilen — kayıpsız PDF olarak açar"
          >
            <Printer size={14} /> PDF Yazdır ({totalLabels}) · Argox
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.15em] text-[#71717A] block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

const inputCls = "w-full px-3 py-2.5 border border-[#E7E5E4] focus:border-[#C8A96A] outline-none text-sm";
